import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { AdyenPaymentCompletedResult } from 'src/components/AdyenPayment/AdyenDropIn';
import { AdyenDropIn } from 'src/components/AdyenPayment/AdyenDropIn';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { PaymentMethodCard } from 'src/components/PaymentMethod';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { typography } from 'src/core/themes/shared.css';
import { Radio, RadioGroup, Stack } from 'src/core/ui';
import {
  useCreateAdyenPaymentSession,
  useIsAdyenConfigured,
  useSubmitAdyenPaymentResult,
  useSubmitPaymentWithStoredMethod,
} from 'src/hooks/api/useAdyenPaymentSession';
import { useApplyExternalPayment } from 'src/hooks/api/useApplyExternalPayment';
import { useGetBuyerCredit } from 'src/hooks/api/useGetBuyerCredit';
import { useGetBuyerPaymentMethods } from 'src/hooks/api/useGetBuyerPaymentMethods';
import { useKnownCountries } from 'src/hooks/api/useKnownCountries';
import {
  useGetRetailSaleDetails,
  useGetSalesInSalesGroup,
} from 'src/hooks/api/useRetailSaleDetails';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Detail } from 'src/modals/common';
import { DetailSection } from 'src/modals/common/DetailSection';
import type { ModalProps } from 'src/modals/Modal';
import {
  ModalBody,
  ModalBodyDataContainer,
  ModalFooter,
} from 'src/modals/Modal/Modal.styled';
import { ModalHeader } from 'src/modals/Modal/ModalHeader';
import { ExternalPaymentSection } from 'src/modals/RetailSale/components/ExternalPaymentSection';
import { PartialPaymentInput } from 'src/modals/RetailSale/components/PartialPaymentInput';
import {
  computeProportionalCoverages,
  getPaymentCountryCode,
} from 'src/modals/RetailSale/RetailSaleLocalContext.utils';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency, roundToPrecision } from 'src/utils/numberFormatter';
import { ExternalPaymentMethodType } from 'src/WebApiController';
import { Feature } from 'src/WebApiController';
import { SaleLineItemCoverage } from 'src/WebApiController';

import * as styles from './MarketplacePaymentDialog.css';
import {
  isSalePaymentMode,
  resolveAdyenPaymentTarget,
  resolveApplyPaymentTarget,
  resolvePaymentInvalidationKeys,
} from './MarketplacePaymentDialog.utils';

/**
 * Sentinel RadioGroup value representing the "add new card" state (i.e. no
 * stored payment method selected). RadioGroup values must be strings, and
 * `selectedStoredPaymentMethodId === null` maps to this value.
 */
const ADD_NEW_CARD_VALUE = '__add_new__';

type ModalConfig = Omit<ModalProps, 'fullscreen'>;

type PayState = 'configuring' | 'paying';

type PaymentMethodChoice = 'card' | 'external' | 'credit';

type AdyenSessionState = {
  sessionId: string;
  sessionData: string;
  clientKey: string;
  environment: string;
  countryCode: string | null;
  lineItemCoverages: SaleLineItemCoverage[] | null;
};

type CommonMarketplacePaymentDialogProps = {
  /** Outstanding balance. Used as the full-payment amount. */
  remainingAmount: number;
  currencyCode: string;
  buyerEmail: string | null;
  cancelTo: ModalConfig | null;
  initialPayState?: PayState;
  initialAdyenSession?: AdyenSessionState | null;
  /**
   * POS-9097: invoked after a successful payment (both modes). Sale-mode callers
   * (opened inside a SaleDetails modal) use it to reload the active POS entity,
   * since the sale view is driven by ActivePosEntityContext rather than the
   * getRetailSaleDetails query key this dialog invalidates. Optional; group
   * callers leave it unset.
   */
  onPaymentSuccess?: () => void | Promise<void>;
  /**
   * Whether the partial-payment toggle is offered. Defaults to true. Launched
   * from the Marketplace Payment view (header / payment-line row action) it is
   * set false so those surfaces only pay the full outstanding balance.
   */
  allowPartialPayment?: boolean;
};

/** Group mode: pays down an EXISTING multi-sale group's balance. */
type GroupModeProps = CommonMarketplacePaymentDialogProps & {
  /** The sales group being paid down. Every payment call is keyed on this. */
  salesGroupId: string;
  saleId?: never;
};

/** Single-sale mode (POS-9097): pays down a TRUE single retail sale. */
type SaleModeProps = CommonMarketplacePaymentDialogProps & {
  /** The single sale being paid down. Every payment call is keyed on this. */
  saleId: number;
  salesGroupId?: never;
};

type GroupMarketplacePaymentDialogProps = GroupModeProps | SaleModeProps;

/**
 * Marketplace payment dialog (POS-8632 group mode; POS-9097 single-sale mode).
 * Pays down an outstanding balance, reusing the exact payment mechanics of the
 * single-sale "Pay Now" dialog: the same leaf components (ExternalPaymentSection,
 * PartialPaymentInput) and the same hooks.
 *
 * Two mutually-exclusive modes, selected by which id prop is passed:
 *  - GROUP mode (`salesGroupId`): coverages are computed across every member
 *    sale of the group (useGetSalesInSalesGroup); every payment call is keyed on
 *    `salesGroupId`; on success it invalidates the group + member-sales queries.
 *  - SALE mode (`saleId`, POS-9097): a TRUE single sale not wrapped in a
 *    group-of-one. Coverages are computed from that one sale's own payable lines
 *    (useGetRetailSaleDetails); every payment call is keyed on `saleId`
 *    (salesGroupId null); on success it invalidates the sale's detail/line-item
 *    queries (NOT the group keys) and calls onPaymentSuccess so the SaleDetails
 *    view can reload its active POS entity.
 *
 * The group-vs-sale keying is centralized in MarketplacePaymentDialog.utils.
 */
export const GroupMarketplacePaymentDialog = ({
  salesGroupId,
  saleId,
  remainingAmount,
  currencyCode,
  buyerEmail,
  cancelTo,
  initialPayState = 'configuring',
  initialAdyenSession = null,
  onPaymentSuccess,
  allowPartialPayment = true,
}: GroupMarketplacePaymentDialogProps) => {
  const isSaleMode = isSalePaymentMode(saleId);
  const queryClient = useQueryClient();
  const { setModal, closeModal } = useContext(ModalContext);
  const { showErrorDialog } = useClientLogContext();

  const { data: knownCountries } = useKnownCountries();
  const countryCode = getPaymentCountryCode(undefined, knownCountries);

  const createPaymentSession = useCreateAdyenPaymentSession();
  const submitPaymentResult = useSubmitAdyenPaymentResult();
  const submitPaymentWithStoredMethod = useSubmitPaymentWithStoredMethod();
  const applyExternalPayment = useApplyExternalPayment();

  const { data: savedPaymentMethods = [] } =
    useGetBuyerPaymentMethods(buyerEmail);

  // Coverage source. In group mode it's every member sale (useGetSalesInSalesGroup);
  // in sale mode it's the single sale's own payable lines (useGetRetailSaleDetails,
  // wrapped in a one-element array so computeProportionalCoverages — which takes an
  // array of {cancellationReason, mkpLines} — is reused unchanged). Only the hook
  // matching the active mode actually runs (each is gated on its own id being set).
  // isFetching gates Pay below: coverages are derived from this data, so a charge
  // must not be submitted while it is refreshing (stale coverages could reference a
  // now-changed line the backend rejects).
  const { data: salesGroupData = [], isFetching: isSalesGroupDataFetching } =
    useGetSalesInSalesGroup(salesGroupId);
  const { data: saleDetailsData, isFetching: isSaleDetailsFetching } =
    useGetRetailSaleDetails(saleId);

  const coverageSalesData = useMemo(
    () =>
      isSaleMode ? (saleDetailsData ? [saleDetailsData] : []) : salesGroupData,
    [isSaleMode, saleDetailsData, salesGroupData]
  );
  const isCoverageDataFetching = isSaleMode
    ? isSaleDetailsFetching
    : isSalesGroupDataFetching;

  // Payment targets — single-key { saleId | salesGroupId } for ApplyExternalPayment,
  // and the { salesGroupId, saleId } pair (one non-null) for the Adyen requests.
  const applyPaymentTarget = resolveApplyPaymentTarget(saleId, salesGroupId);
  const adyenPaymentTarget = resolveAdyenPaymentTarget(saleId, salesGroupId);

  // Gate the payment-mode radio behind the RetailSale feature flag, mirroring
  // the group-creation payment step (RetailSalePaymentSection).
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  // Hide the "Paid in POS" (Adyen) option unless the seller has an active Adyen
  // processor configured. Aliased because the component has its own `isLoading`
  // state for in-flight payment mutations.
  const { data: isAdyenConfigured, isLoading: isAdyenConfigLoading } =
    useIsAdyenConfigured();
  const showCardOption = isAdyenConfigured === true;

  // POS-8726: Fetch buyer credit balance for "Pay by Customer Credit" option
  const { data: buyerCredit } = useGetBuyerCredit(
    buyerEmail,
    !hasRetailSaleFeature,
    currencyCode // Convert credit to the sale's currency for display
  );
  const availableCreditBalance = buyerCredit?.balance ?? 0;

  const paymentMethodMsg = useContent(ContentId.PaymentMethod);
  const creditPaymentNote = useContent(ContentId.PaidViaCustomerCredit);
  const customerRemainingCreditLabel = useContent(
    ContentId.CustomerRemainingCredit
  );

  const paymentMsg = useContent(ContentId.Payment);
  const adyenWithNoCvvMsg = useContent(ContentId.AdyenWithNoCvv);
  const adyenWithCvvMsg = useContent(ContentId.AdyenWithCvv);
  const paymentAmountText = useContent(ContentId.PaymentAmount);

  // ── Shared state ──────────────────────────────────────────────────────────
  const [paymentMethodChoice, setPaymentMethodChoice] =
    useState<PaymentMethodChoice>('card');
  const [payState, setPayState] = useState<PayState>(initialPayState);
  const [adyenSession, setAdyenSession] = useState<AdyenSessionState | null>(
    initialAdyenSession ?? null
  );
  const [isLoading, setIsLoading] = useState(false);
  // Set when a card charge was accepted but Adyen has not yet returned a final
  // (synchronous) authorisation — the payment settles shortly via the Adyen
  // webhook. We keep the dialog open, disable Pay so the charge is not
  // re-submitted, and show a "finalizing" message.
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [partialAmount, setPartialAmount] = useState(remainingAmount);
  const [selectedStoredPaymentMethodId, setSelectedStoredPaymentMethodId] =
    useState<string | null>(null);
  const merchantRef = useRef<string | null>(null);

  // ── External payment state (useState, no react-hook-form) ─────────────────
  const [externalPaymentMethodType, setExternalPaymentMethodType] = useState<
    ExternalPaymentMethodType | undefined
  >(undefined);
  const [externalPaymentNote, setExternalPaymentNote] = useState<string>('');
  const [customPaymentTypeName, setCustomPaymentTypeName] = useState<
    string | undefined
  >(undefined);
  const [customPaymentTypeId, setCustomPaymentTypeId] = useState<
    number | undefined
  >(undefined);
  const [externalPaymentDate, setExternalPaymentDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [externalMethodTypeError, setExternalMethodTypeError] = useState<
    string | undefined
  >(undefined);

  const handlePaymentChoiceChange = (value: string) => {
    setPaymentMethodChoice(value as PaymentMethodChoice);
    // Reset external validation error when switching modes
    setExternalMethodTypeError(undefined);
  };

  // When Adyen isn't configured (or the feature is off), the "Paid in POS"
  // option is hidden — fall the default 'card' selection back to 'external'.
  useEffect(() => {
    if (isAdyenConfigLoading || showCardOption) return;
    setPaymentMethodChoice((prev) => (prev === 'card' ? 'external' : prev));
  }, [isAdyenConfigLoading, showCardOption]);

  const handleClose = () => {
    setAdyenSession(null);
    merchantRef.current = null;
    if (cancelTo) {
      setModal(cancelTo);
    } else {
      closeModal(true);
    }
  };

  // ── Invalidate and refresh after a payment ────────────────────────────────
  // Group mode: invalidate the group payload + its member-sales list.
  // Sale mode (POS-9097): invalidate the single sale's detail/line-item queries
  // (NOT the group keys). The active SaleDetails view is driven by
  // ActivePosEntityContext, so sale-mode callers pass onPaymentSuccess to reload
  // it. See resolvePaymentInvalidationKeys.
  const invalidateAndRefresh = async () => {
    const keys = resolvePaymentInvalidationKeys(saleId, salesGroupId);
    await Promise.all(
      keys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))
    );
    await onPaymentSuccess?.();
  };

  // ── External partial: coverage confirmed ─────────────────────────────────
  const handleExternalCoverageConfirmed = async (
    coverages: SaleLineItemCoverage[],
    paymentAmount: number
  ) => {
    setIsLoading(true);
    try {
      // ApplyExternalPayment is keyed on the active target (saleId or salesGroupId)
      // and settles the outstanding retail header via the shared chain-of-headers
      // path, matching the Adyen flow.
      await applyExternalPayment.mutateAsync({
        ...applyPaymentTarget,
        input: {
          externalPaymentMethodType: externalPaymentMethodType!,
          externalPaymentNote: externalPaymentNote || undefined,
          customPaymentTypeName: customPaymentTypeName ?? null,
          customPaymentTypeId: customPaymentTypeId ?? null,
          externalPaymentAmount: paymentAmount,
          externalPaymentDate: externalPaymentDate
            ? new Date(externalPaymentDate).toISOString()
            : undefined,
          coverages,
        },
      });
      await invalidateAndRefresh();
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceed = async () => {
    if (!merchantRef.current) {
      // Merchant reference is sale-based in both modes: the single sale in sale
      // mode, the representative member sale in group mode.
      merchantRef.current = `Retail-${saleId ?? salesGroupId}-${Date.now()}`;
    }

    // ── Customer Credit flow ─────────────────────────────────────────────
    if (paymentMethodChoice === 'credit') {
      // Round to cents — see the Adyen path below; currency amounts summed as JS
      // floats can carry a sub-cent artifact.
      const creditPayAmount = roundToPrecision(
        isPartialPayment ? partialAmount : remainingAmount,
        2
      );
      setIsLoading(true);
      try {
        if (isPartialPayment) {
          // Partial credit: auto-compute proportional coverages and apply
          const coverages = computeProportionalCoverages(
            creditPayAmount,
            coverageSalesData
          );
          await applyExternalPayment.mutateAsync({
            ...applyPaymentTarget,
            input: {
              externalPaymentMethodType:
                ExternalPaymentMethodType.CustomerCredit,
              externalPaymentNote: creditPaymentNote,
              customPaymentTypeName: null,
              customPaymentTypeId: null,
              externalPaymentAmount: creditPayAmount,
              coverages,
            },
          });
        } else {
          // Full credit: apply to full remaining amount
          await applyExternalPayment.mutateAsync({
            ...applyPaymentTarget,
            input: {
              externalPaymentMethodType:
                ExternalPaymentMethodType.CustomerCredit,
              externalPaymentNote: creditPaymentNote,
              customPaymentTypeName: null,
              customPaymentTypeId: null,
              externalPaymentAmount: creditPayAmount,
              coverages: undefined,
            },
          });
        }
        await invalidateAndRefresh();
        merchantRef.current = null;
        handleClose();
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // ── External payment flow ─────────────────────────────────────────────
    if (paymentMethodChoice === 'external') {
      // Validate that a method type has been selected
      if (externalPaymentMethodType == null) {
        setExternalMethodTypeError('Payment Method Type is required');
        return;
      }
      setExternalMethodTypeError(undefined);

      if (isPartialPayment) {
        // External + partial: auto-compute proportional coverages and apply.
        // Round to cents so the applied amount and the per-line coverages agree.
        const roundedPartial = roundToPrecision(partialAmount, 2);
        const coverages = computeProportionalCoverages(
          roundedPartial,
          coverageSalesData
        );
        await handleExternalCoverageConfirmed(coverages, roundedPartial);
        return;
      }

      // External + full: call ApplyExternalPayment directly
      setIsLoading(true);
      try {
        // ApplyExternalPayment is keyed on the active target (saleId or salesGroupId)
        // and settles the outstanding retail header via the shared chain-of-headers
        // path, matching the Adyen flow.
        await applyExternalPayment.mutateAsync({
          ...applyPaymentTarget,
          input: {
            externalPaymentMethodType: externalPaymentMethodType,
            externalPaymentNote: externalPaymentNote || undefined,
            customPaymentTypeName: customPaymentTypeName ?? null,
            customPaymentTypeId: customPaymentTypeId ?? null,
            externalPaymentAmount: roundToPrecision(remainingAmount, 2),
            externalPaymentDate: externalPaymentDate
              ? new Date(externalPaymentDate).toISOString()
              : undefined,
            coverages: undefined,
          },
        });
        await invalidateAndRefresh();
        merchantRef.current = null;
        handleClose();
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // ── Adyen flow (card) — keyed on the active target (saleId or salesGroupId) ──
    setIsLoading(true);

    // Always settle via line-item coverages so a follow-up payment on an already
    // partially-paid group flows through the coverage branch (never the full-
    // settlement path, which only settles a fully-Unpaid group). A partial payment
    // covers a proportional slice; a full payment covers every remaining unpaid line.
    // Round to cents: the amount is a JS float summed from line amounts and can
    // carry a sub-cent artifact (e.g. 5.43 + 7.29 === 12.719999999999999).
    // Coverages are rounded per-line, so an un-rounded amount fails the backend's
    // exact "amount === sum(coverages)" guard in ValidateCoverages.
    const paymentAmount = roundToPrecision(
      isPartialPayment ? partialAmount : remainingAmount,
      2
    );
    const coverages = computeProportionalCoverages(
      paymentAmount,
      coverageSalesData
    );
    try {
      if (selectedStoredPaymentMethodId) {
        const paymentStatus = await submitPaymentWithStoredMethod.mutateAsync({
          ...adyenPaymentTarget,
          storedPaymentMethodId: selectedStoredPaymentMethodId,
          amount: paymentAmount,
          currencyCode,
          buyerEmail,
          reference: merchantRef.current,
          lineItemCoverages: coverages,
        });
        if (!paymentStatus.settled) {
          // Charge accepted but not yet settled (async Adyen authorisation). The
          // webhook settles it shortly; disable Pay (via isFinalizing) so the same
          // reference is not re-submitted, and show an inline message.
          setIsFinalizing(true);
        } else {
          await invalidateAndRefresh();
          merchantRef.current = null;
          handleClose();
        }
      } else {
        const session = await createPaymentSession.mutateAsync({
          ...adyenPaymentTarget,
          amount: paymentAmount,
          currencyCode,
          buyerEmail,
          returnUrl: window.location.href,
          reference: merchantRef.current,
          countryCode,
          lineItemCoverages: coverages,
        });
        setAdyenSession({
          ...session,
          countryCode,
          lineItemCoverages: coverages,
        });
        setPayState('paying');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onPaymentCompleted = async (result: AdyenPaymentCompletedResult) => {
    if (!adyenSession) return;
    if (result.resultCode !== 'Authorised') {
      showErrorDialog('AdyenPayment', {
        message: `Payment not authorized: ${result.resultCode}`,
      });
      setAdyenSession(null);
      setPayState('configuring');
      return;
    }
    setIsLoading(true);
    try {
      const paymentStatus = await submitPaymentResult.mutateAsync({
        // Null in sale mode — HandlePaymentResult resolves the target by
        // MerchantReference; this id is only used for logging.
        salesGroupId: adyenPaymentTarget.salesGroupId,
        input: {
          resultCode: result.resultCode,
          sessionResult: result.sessionResult ?? null,
          merchantReference: merchantRef.current ?? null,
          pspReference: null,
          lineItemCoverages: adyenSession.lineItemCoverages,
        },
      });
      if (!paymentStatus.settled) {
        showErrorDialog('AdyenPayment', {
          message:
            'Payment was received but is still being finalized. Please check the sale status in a moment.',
        });
      } else {
        await invalidateAndRefresh();
        merchantRef.current = null;
        handleClose();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isCreditPayment = paymentMethodChoice === 'credit';

  const isProceedDisabled = useMemo(() => {
    // Block while the coverage source is (re)loading — coverages are derived from
    // it, so submitting mid-refresh could send stale coverages.
    if (isLoading || isFinalizing || isCoverageDataFetching) return true;
    if (
      isPartialPayment &&
      (partialAmount <= 0 || partialAmount >= remainingAmount)
    )
      return true;
    // Credit payment: buyer must have enough credit to cover the payment amount
    const creditRequired = isPartialPayment ? partialAmount : remainingAmount;
    if (isCreditPayment && availableCreditBalance < creditRequired) return true;
    return false;
  }, [
    isLoading,
    isFinalizing,
    isCoverageDataFetching,
    isPartialPayment,
    partialAmount,
    remainingAmount,
    isCreditPayment,
    availableCreditBalance,
  ]);

  const isExternalPayment = paymentMethodChoice === 'external';

  return (
    <>
      <ModalHeader onClose={handleClose}>
        <div className={styles.headerBar}>
          <h5 className={clsx(typography.title5, styles.headerText)}>
            <Content id={ContentId.AddPayment} />
          </h5>
        </div>
      </ModalHeader>
      <ModalBody>
        <ModalBodyDataContainer style={{ height: '100%' }}>
          {payState === 'configuring' && (
            <DetailSection name={paymentMsg}>
              {allowPartialPayment && (
                <Detail
                  label={paymentAmountText}
                  detail={
                    <PartialPaymentInput
                      disabled={isLoading}
                      totalAmount={remainingAmount}
                      currencyCode={currencyCode}
                      isPartialPayment={isPartialPayment}
                      partialAmount={partialAmount}
                      onIsPartialPaymentChange={setIsPartialPayment}
                      onPartialAmountChange={setPartialAmount}
                    />
                  }
                />
              )}
              <Detail
                label={paymentMethodMsg}
                detail={
                  <Stack direction="column" gap="m">
                    {/* Top-level payment method radio — gated behind RetailSale,
                    matching the group-creation payment step. */}
                    <RadioGroup
                      value={paymentMethodChoice}
                      onValueChange={handlePaymentChoiceChange}
                      disabled={isLoading}
                    >
                      <Stack width="full" direction="column" gap="m">
                        {showCardOption && (
                          <>
                            <Radio
                              alignItems="start"
                              variant="outline"
                              value="card"
                              label={adyenWithNoCvvMsg}
                              containerClassName={styles.paymentItem}
                              labelSubtext={
                                paymentMethodChoice === 'card' && (
                                  <Stack
                                    width="full"
                                    direction="column"
                                    gap="m"
                                  >
                                    <RadioGroup
                                      value={
                                        selectedStoredPaymentMethodId ??
                                        ADD_NEW_CARD_VALUE
                                      }
                                      onValueChange={(value) =>
                                        setSelectedStoredPaymentMethodId(
                                          value === ADD_NEW_CARD_VALUE
                                            ? null
                                            : value
                                        )
                                      }
                                      disabled={isLoading}
                                    >
                                      {savedPaymentMethods.map((card) => (
                                        <Radio
                                          key={card.storedPaymentMethodId}
                                          value={card.storedPaymentMethodId}
                                          variant="outline"
                                          disabled={isLoading}
                                          label={
                                            <PaymentMethodCard
                                              flattened
                                              cardBrand={card.cardBrand}
                                              cardSummary={card.cardSummary}
                                              expiryMonth={card.expiryMonth}
                                              expiryYear={card.expiryYear}
                                              isDefault={card.isDefault}
                                            />
                                          }
                                        />
                                      ))}
                                    </RadioGroup>
                                  </Stack>
                                )
                              }
                              disabled={isLoading}
                            />
                            <Radio
                              alignItems="start"
                              value={ADD_NEW_CARD_VALUE}
                              containerClassName={styles.paymentItem}
                              variant="outline"
                              disabled={isLoading}
                              label={adyenWithCvvMsg}
                            />
                          </>
                        )}
                        <Radio
                          variant="outline"
                          value="external"
                          label="Paid Externally (Cash / Wire / Other)"
                          containerClassName={styles.paymentItem}
                          labelSubtext={
                            isExternalPayment && (
                              <ExternalPaymentSection
                                disabled={isLoading}
                                totalAmount={
                                  isPartialPayment
                                    ? partialAmount
                                    : remainingAmount
                                }
                                controlled={{
                                  methodType: externalPaymentMethodType,
                                  note: externalPaymentNote,
                                  date: externalPaymentDate,
                                  currencyCode,
                                  customPaymentTypeName,
                                  customPaymentTypeId,
                                  methodTypeError: externalMethodTypeError,
                                  onMethodTypeChange:
                                    setExternalPaymentMethodType,
                                  onNoteChange: setExternalPaymentNote,
                                  onDateChange: setExternalPaymentDate,
                                  onCustomPaymentTypeNameChange:
                                    setCustomPaymentTypeName,
                                  onCustomPaymentTypeIdChange:
                                    setCustomPaymentTypeId,
                                }}
                              />
                            )
                          }
                          disabled={isLoading}
                        />
                        <Radio
                          alignItems="start"
                          variant="outline"
                          value="credit"
                          label="Pay by Customer Credit"
                          containerClassName={styles.paymentItem}
                          labelSubtext={
                            <>
                              <span className={styles.creditBalanceHint}>
                                {`${customerRemainingCreditLabel}: ${formatCurrency(availableCreditBalance, currencyCode)}`}
                              </span>
                              {allowPartialPayment &&
                                paymentMethodChoice === 'credit' && (
                                  <PartialPaymentInput
                                    disabled={isLoading}
                                    totalAmount={remainingAmount}
                                    currencyCode={currencyCode}
                                    isPartialPayment={isPartialPayment}
                                    partialAmount={partialAmount}
                                    onIsPartialPaymentChange={
                                      setIsPartialPayment
                                    }
                                    onPartialAmountChange={setPartialAmount}
                                  />
                                )}
                            </>
                          }
                          disabled={isLoading}
                        />
                      </Stack>
                    </RadioGroup>
                  </Stack>
                }
              />
            </DetailSection>
          )}

          {payState === 'paying' && adyenSession && (
            <div className={styles.adyenContainer}>
              <AdyenDropIn
                sessionId={adyenSession.sessionId}
                sessionData={adyenSession.sessionData}
                clientKey={adyenSession.clientKey}
                environment={adyenSession.environment}
                countryCode={adyenSession.countryCode}
                onPaymentCompleted={onPaymentCompleted}
                onError={(error) => {
                  showErrorDialog('AdyenPayment', { message: error.message });
                  setAdyenSession(null);
                  setPayState('configuring');
                }}
              />
            </div>
          )}
        </ModalBodyDataContainer>
      </ModalBody>
      <ModalFooter>
        <Stack direction="column" gap="s" width="full">
          {isFinalizing && (
            <span className={typography.body2}>
              Payment received — finalizing. It will appear on the sale shortly;
              please refresh in a moment.
            </span>
          )}
          <Stack justifyContent="end" gap="m">
            <CancelButton onClick={handleClose} disabled={isLoading} />
            {payState === 'configuring' && (
              <OkButton
                textContentId={ContentId.Confirm}
                variant="regular"
                onClick={handleProceed}
                disabled={isProceedDisabled}
              />
            )}
          </Stack>
        </Stack>
      </ModalFooter>
    </>
  );
};
